import BlankSlate from "@pathwright/ui/src/components/blank/BlankSlate"
import Card from "@pathwright/ui/src/components/card/Card"
import CardHeader from "@pathwright/ui/src/components/card/CardHeader"
import styled from "styled-components"
import { OrderFragment, useScopedResourceLicenseQuery } from "@pathwright/graphql-client/src/generated"
import { useCurrencyFormatter } from "../currency/CurrencyFormatterProvider"
import { usePathwrightContext } from "../pathwright/PathwrightContext"
import { getSwitchSpaceLink } from "../space/switcher/SpaceSwitcher"
import { getGroupUrl } from "../utils/urls"
import OrderProcessing, { OrderProcessingLoading } from "./OrderProcessing"
import { OnOrderFulfilledCallback } from "./useOrderPollingQuery"

const NarrowCard = styled(Card)`
  & > .View {
    max-width: 500px;
  }
`

function useOrderResourceLicense(
  order: OrderFragment & { fulfilled_object_id: number }
) {
  const resourceLicenseQuery = useScopedResourceLicenseQuery({
    skip: !order.fulfilled_object_id,
    variables: {
      schoolId: order.school_id,
      resourceLicenseId: order.fulfilled_object_id
    }
  })
  return resourceLicenseQuery.data?.context?.school?.resource_license ?? null
}

export function CurriculumLicenseOrderConfirmationContent({
  order
}: {
  order: OrderFragment & { fulfilled_object_id: number }
}) {
  const { school } = usePathwrightContext()
  const resourceLicense = useOrderResourceLicense(order)
  const ssoLink = getSwitchSpaceLink(order?.school_id)
  const group = resourceLicense?.group
  const licenseSchool = resourceLicense?.school

  if (!resourceLicense || !group || !licenseSchool) {
    return <OrderProcessingLoading />
  }

  const isSameSchool = licenseSchool.id === school.id

  return (
    <BlankSlate
      animateIcon
      stack
      icons={null}
      icon="check"
      heading={`${group.name} is ready to teach!`}
      body={`The curriculum is ready to offer under your account, ${licenseSchool.name}`}
      {...(isSameSchool
        ? {
            primaryAction: {
              to: getGroupUrl(group.resource?.slug || "", group.id || 0),
              children: "Go To Your Course"
            }
          }
        : {
            primaryAction: {
              href: ssoLink,
              children: "Go To Your Course"
            },
            secondaryAction: {
              brand: true,
              to: "/store/",
              children: "Or continue browsing"
            }
          })}
    />
  )
}

export function CurriculumLicenseOrderConfirmation({
  orderId,
  onOrderFulfilled
}: {
  orderId: number
  onOrderFulfilled?: OnOrderFulfilledCallback
}) {
  return (
    <OrderProcessing orderId={orderId} onOrderFulfilled={onOrderFulfilled}>
      {({ order }) => (
        <CurriculumLicenseOrderConfirmationContent order={order} />
      )}
    </OrderProcessing>
  )
}

function CurriculumLicenseOrderConfirmationCardHeader({
  order,
  card
}: {
  order: OrderFragment & { fulfilled_object_id: number }
  card: any
}) {
  const { currencyFormatter } = useCurrencyFormatter()
  const resourceLicense = useOrderResourceLicense(order)
  const group = resourceLicense?.group
  const license = resourceLicense?.license_offering

  if (!group || !license) {
    return null
  }

  return (
    <CardHeader
      card={card}
      title={group.name || ""}
      meta={
        (license.cost_per_seat || 0) > 0
          ? `Curriculum License • ${currencyFormatter.customFormat(
              license.cost_per_seat
            )}/member`
          : "Curriculum License"
      }
    />
  )
}

export default function CurriculumLicenseOrderConfirmationCard({
  card,
  orderId,
  onOrderFulfilled
}: {
  card: any
  orderId: number
  onOrderFulfilled?: OnOrderFulfilledCallback
}) {
  return (
    <NarrowCard card={card} navless>
      <OrderProcessing orderId={orderId} onOrderFulfilled={onOrderFulfilled}>
        {({ order }) => (
          <>
            <CurriculumLicenseOrderConfirmationCardHeader
              order={order}
              card={card}
            />
            <CurriculumLicenseOrderConfirmationContent order={order} />
          </>
        )}
      </OrderProcessing>
    </NarrowCard>
  )
}

CurriculumLicenseOrderConfirmationCard.displayName =
  "CurriculumLicenseOrderConfirmationCard"
